import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Chip,
  IconButton,
  Menu,
  MenuItem as MenuItemComponent,
  CircularProgress
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon
} from '@mui/icons-material'
import { DataGrid } from '@mui/x-data-grid'
import { usersAPI } from '../services/api'

function UserManagement() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [openDialog, setOpenDialog] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedUser, setSelectedUser] = useState(null)
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    role: 'editor',
    is_active: true
  })

  useEffect(() => {
    loadUsers()
  }, [])

  const loadUsers = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await usersAPI.getAll()
      if (response.success) {
        setUsers(response.data)
      } else {
        setError('Failed to load users')
      }
    } catch (error) {
      console.error('Error loading users:', error)
      setError('Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  const handleOpenDialog = (user = null) => {
    setEditingUser(user)
    if (user) {
      setFormData({
        username: user.username,
        email: user.email,
        full_name: user.full_name,
        password: '',
        role: user.role,
        is_active: user.is_active === 1
      })
    } else {
      setFormData({
        username: '',
        email: '',
        full_name: '',
        password: '',
        role: 'editor',
        is_active: true
      })
    }
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditingUser(null)
    setFormData({
      username: '',
      email: '',
      full_name: '',
      password: '',
      role: 'editor',
      is_active: true
    })
  }

  const handleSubmit = async () => {
    try {
      const userData = {
        ...formData,
        is_active: formData.is_active ? 1 : 0
      }

      if (editingUser) {
        // Update user
        if (!userData.password) {
          delete userData.password // Don't update password if empty
        }
        const response = await usersAPI.update(editingUser.id, userData)
        if (response.success) {
          await loadUsers()
          handleCloseDialog()
        } else {
          setError(response.message || 'Failed to update user')
        }
      } else {
        // Create user
        const response = await usersAPI.create(userData)
        if (response.success) {
          await loadUsers()
          handleCloseDialog()
        } else {
          setError(response.message || 'Failed to create user')
        }
      }
    } catch (error) {
      console.error('Error saving user:', error)
      setError('Failed to save user')
    }
  }

  const handleMenuClick = (event, user) => {
    setAnchorEl(event.currentTarget)
    setSelectedUser(user)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedUser(null)
  }

  const handleToggleStatus = async (user) => {
    try {
      const newStatus = user.is_active === 1 ? 0 : 1
      const response = await usersAPI.updateStatus(user.id, newStatus)
      if (response.success) {
        await loadUsers()
      } else {
        setError('Failed to update user status')
      }
    } catch (error) {
      console.error('Error updating user status:', error)
      setError('Failed to update user status')
    }
    handleMenuClose()
  }

  const handleDeleteUser = async (user) => {
    if (window.confirm(`Are you sure you want to delete user "${user.username}"?`)) {
      try {
        const response = await usersAPI.delete(user.id)
        if (response.success) {
          await loadUsers()
        } else {
          setError('Failed to delete user')
        }
      } catch (error) {
        console.error('Error deleting user:', error)
        setError('Failed to delete user')
      }
    }
    handleMenuClose()
  }

  const handleResetPassword = async (user) => {
    const newPassword = prompt(`Enter new password for ${user.username}:`)
    if (newPassword) {
      try {
        const response = await usersAPI.resetPassword(user.id, newPassword)
        if (response.success) {
          alert('Password reset successfully')
        } else {
          setError('Failed to reset password')
        }
      } catch (error) {
        console.error('Error resetting password:', error)
        setError('Failed to reset password')
      }
    }
    handleMenuClose()
  }

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'username', headerName: 'Username', width: 150 },
    { field: 'email', headerName: 'Email', width: 200 },
    { field: 'full_name', headerName: 'Full Name', width: 200 },
    {
      field: 'role',
      headerName: 'Role',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={
            params.value === 'super_admin' ? 'error' :
            params.value === 'admin' ? 'warning' : 'default'
          }
          size="small"
        />
      )
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value === 1 ? 'Active' : 'Inactive'}
          color={params.value === 1 ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      renderCell: (params) => new Date(params.value).toLocaleDateString()
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuClick(e, params.row)}
        >
          <MoreVertIcon />
        </IconButton>
      )
    }
  ]

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">User Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={users}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              loading={loading}
              disableSelectionOnClick
              sx={{
                '& .MuiDataGrid-cell:hover': {
                  color: 'primary.main',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* User Form Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Full Name"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label={editingUser ? "New Password (leave empty to keep current)" : "Password"}
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              margin="normal"
              required={!editingUser}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                label="Role"
              >
                <MenuItem value="editor">Editor</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="super_admin">Super Admin</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItemComponent onClick={() => handleOpenDialog(selectedUser)}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit
        </MenuItemComponent>
        <MenuItemComponent onClick={() => handleToggleStatus(selectedUser)}>
          {selectedUser?.is_active === 1 ? (
            <>
              <LockIcon sx={{ mr: 1 }} fontSize="small" />
              Deactivate
            </>
          ) : (
            <>
              <LockOpenIcon sx={{ mr: 1 }} fontSize="small" />
              Activate
            </>
          )}
        </MenuItemComponent>
        <MenuItemComponent onClick={() => handleResetPassword(selectedUser)}>
          <LockIcon sx={{ mr: 1 }} fontSize="small" />
          Reset Password
        </MenuItemComponent>
        <MenuItemComponent 
          onClick={() => handleDeleteUser(selectedUser)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItemComponent>
      </Menu>
    </Box>
  )
}

export default UserManagement